<template>
  <div>
    <page-header :title="title" :container-class="containerClass" />
    <div class="page-section">
      <div :class="containerClass">
        <div class="card mb-0">
          <div class="card-header">
            <div class="row align-items-center" style="white-space: nowrap">
              <div class="col-lg-auto">
                <form class="search-form search-form--light d-lg-inline-flex mb-8pt mb-lg-0" @submit.prevent="">
                  <b-form-input
                    class="w-lg-auto"
                    placeholder="Search Enrollments"
                    v-model="searchTerm"
                    @input="onSearch"
                  />
                  <b-btn variant="flush" type="submit">
                    <md-icon v-text="'search'" />
                  </b-btn>
                </form>
              </div>
            </div>
          </div>

          <b-table
            :fields="tableColumns"
            :items="enrollments"
            :busy="isLoading"
            head-variant="light"
            class="table-nowrap"
            hover
            responsive
            no-local-sorting
          >
            <template #cell(student)="data">
              <strong>{{ studentName(data.item) }}</strong>
              <div class="text-muted small" v-if="studentEmail(data.item)">
                {{ studentEmail(data.item) }}
              </div>
            </template>

            <template #cell(pathway)="data">
              {{ get(data.item, 'pathway.title', '—') }}
            </template>

            <template #cell(progression)="data">
              <strong>{{ get(data.item, 'pathway.from_program.title', '—') }}</strong>
              <md-icon class="mx-1" style="font-size: 14px;">arrow_forward</md-icon>
              <strong>{{ get(data.item, 'pathway.to_program.title', '—') }}</strong>
            </template>

            <template #cell(school)="data">
              {{ get(data.item, 'school.name', '—') }}
            </template>

            <template #cell(created_at)="data">
              {{ formatDate(data.item.created_at) }}
            </template>
          </b-table>

          <div class="card-footer">
            <pagination
              v-model="currentPage"
              :total-rows="totalEnrollments"
              :per-page="perPage"
              @change="onPageChange"
              aria-controls="enrollments-table"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PageHeader from '@/components/Ui/PageHeader.vue';
import { debounce, get } from 'lodash';
import { mapActions } from 'vuex';

import Page from '@/components/Page.vue';
import Pagination from '@/components/Ui/Pagination.vue';
import MdIcon from '@/components/Ui/MdIcon.vue';
import { DEFAULT_PAGE_SIZE } from '@/common/constants';

export default {
  components: { MdIcon, PageHeader, Pagination },
  extends: Page,

  data() {
    return {
      title: 'Pathway Enrollments',
      isLoading: false,
      perPage: DEFAULT_PAGE_SIZE,
      currentPage: 1,
      totalEnrollments: 0,
      searchTerm: '',
      enrollments: [],
    };
  },

  computed: {
    tableColumns() {
      return [
        { key: 'student', label: 'Student' },
        { key: 'pathway', label: 'Pathway' },
        { key: 'progression', label: 'From → To' },
        { key: 'school', label: 'School' },
        { key: 'created_at', label: 'Enrolled' },
      ];
    },
  },

  methods: {
    get,
    ...mapActions('pathways', ['getAllPathwayEnrollments']),

    studentName(item) {
      const first = get(item, 'student.first_name', '') || get(item, 'student.user.first_name', '');
      const last = get(item, 'student.last_name', '') || get(item, 'student.user.last_name', '');
      const full = `${first} ${last}`.trim();
      return full || this.studentEmail(item) || '—';
    },

    studentEmail(item) {
      return get(item, 'student.email', '') || get(item, 'student.user.email', '');
    },

    formatDate(value) {
      if (!value) return '—';
      try {
        return new Date(value).toLocaleDateString();
      } catch (_e) {
        return value;
      }
    },

    async fetchEnrollments(pageNum = 1) {
      this.isLoading = true;
      try {
        const response = await this.getAllPathwayEnrollments({
          limit: this.perPage,
          offset: (pageNum - 1) * this.perPage,
          ...(this.searchTerm && { search: this.searchTerm }),
        });
        this.enrollments = get(response, 'data.results', []);
        this.currentPage = pageNum;
        this.totalEnrollments = get(response, 'data.count', 0);
      } catch (_err) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.isLoading = false;
    },

    onPageChange(pageNum) {
      this.fetchEnrollments(pageNum);
    },

    onSearch() {
      this.debouncedSearch(this);
    },

    debouncedSearch: debounce(vm => {
      vm.fetchEnrollments();
    }, 500),
  },

  async mounted() {
    this.fetchEnrollments();
  },
};
</script>
